import { useEffect, useMemo, useRef, useState } from 'react'
import type { NarrativeGraph } from '@/features/warroom/api'

/**
 * The live pulse — the social window as arriving weather.
 *
 * ## Why this one thing on the page moves
 *
 * Everything else here is a record and holds still; motion over a record is decoration
 * pretending to be information. The social feed is the one quantity on this dashboard that
 * is changing while somebody watches it, and the useful reading is a RATE — so this is the
 * only animated element, and every part of the animation carries a fact:
 *
 * - **A mote per thread**, drifting right at a speed set by its own velocity. Fast threads
 *   visibly outrun slow ones; that comparison is the whole point and a bar chart cannot make
 *   it.
 * - **Size is engagements**, on a square-root scale, so area rather than radius carries the
 *   quantity.
 * - **Height is threat.** The threshold line is drawn, so "above the line" is literal.
 * - **A ring means nothing has been drafted** against a thread that is above the line — the
 *   one state on this strip that is a job rather than an observation.
 * - **Hollow, dimmer motes are unread.** They are drawn, because a strip that showed only
 *   scored threads would render a blind spot as calm — the exact failure the war room exists
 *   to prevent.
 *
 * ## It stops when it should
 *
 * `prefers-reduced-motion` freezes it to a still frame with the same information. It also
 * stops when the tab is hidden, because a canvas loop in a background tab is a battery cost
 * with no reader.
 */
interface Mote {
  x: number
  y: number
  r: number
  speed: number
  threat: number | null
  unanswered: boolean
  hue: string
}

export function LivePulse({ graph, height = 128 }: { graph: NarrativeGraph | null; height?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const motesRef = useRef<Mote[]>([])
  const rafRef = useRef<number>(0)
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(m.matches)
    const on = () => setReduced(m.matches)
    m.addEventListener('change', on)
    return () => m.removeEventListener('change', on)
  }, [])

  const seed = useMemo(() => {
    if (!graph) return []
    const threads = graph.threads.slice(0, 120)
    const maxEng = Math.max(...threads.map((t) => t.engagements ?? 0), 1)
    const maxVel = Math.max(...threads.map((t) => t.velocity ?? 0), 0.1)
    return threads.map((t) => {
      const above = t.threat != null && t.threat >= graph.deployThreshold
      return {
        // Positions are randomised once, at seed time, and then only advanced — re-rolling
        // them on a re-render would make the strip twitch on every poll.
        x: Math.random(),
        threat: t.threat,
        // A log speed, because velocity is long-tailed: one 6/min thread against a field
        // under 0.1 would otherwise be the only thing that appears to move at all.
        speed: 0.15 + (Math.log1p(t.velocity ?? 0) / Math.log1p(maxVel)) * 0.85,
        r: 2 + Math.sqrt((t.engagements ?? 0) / maxEng) * 7,
        unanswered: above && t.draftCount === 0 && t.deployedCount === 0,
        hue: t.threat == null ? 'unread' : above ? 'hot' : 'cool',
      }
    })
  }, [graph])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || seed.length === 0) return

    const css = getComputedStyle(document.documentElement)
    const ink = (name: string, fallback: string) =>
      css.getPropertyValue(name).trim() || fallback
    const COLOUR = {
      hot: ink('--dash-hot', '#e11d48'),
      cool: ink('--dash-cool', '#0284c7'),
      unread: ink('--dash-unread', '#94a3b8'),
    }

    let w = 0
    let h = 0
    const dpr = Math.min(window.devicePixelRatio || 1, 2)

    const size = () => {
      const rect = canvas.getBoundingClientRect()
      w = rect.width
      h = rect.height
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
    }
    size()

    motesRef.current = seed.map((s) => ({
      ...s,
      x: s.x * Math.max(w, 1),
      // Threat sets the height; unread motes sit in a band of their own at the bottom so
      // they are visibly NOT low-threat, which is a different statement from a low position.
      y: s.threat == null
        ? h * 0.86 + (Math.random() - 0.5) * (h * 0.12)
        : h - (s.threat / 10) * (h * 0.82) - h * 0.08,
    }))

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const threshold = graph ? graph.deployThreshold / 10 : 0.7

    const draw = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      // The threshold, drawn once and labelled by the caption beneath.
      const ty = h - threshold * (h * 0.82) - h * 0.08
      ctx.strokeStyle = COLOUR.hot
      ctx.globalAlpha = 0.28
      ctx.setLineDash([4, 4])
      ctx.beginPath()
      ctx.moveTo(0, ty)
      ctx.lineTo(w, ty)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.globalAlpha = 1

      for (const m of motesRef.current) {
        if (!reduced) {
          m.x += m.speed
          if (m.x - m.r > w) m.x = -m.r
        }
        const c = COLOUR[m.hue as keyof typeof COLOUR]
        ctx.beginPath()
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2)
        if (m.hue === 'unread') {
          // Hollow: an unread thread is an absence of information, and filling it in would
          // draw it as a measured low-threat one.
          ctx.strokeStyle = c
          ctx.globalAlpha = 0.55
          ctx.lineWidth = 1.25
          ctx.stroke()
        } else {
          ctx.fillStyle = c
          ctx.globalAlpha = 0.5
          ctx.fill()
        }
        ctx.globalAlpha = 1

        if (m.unanswered) {
          ctx.beginPath()
          ctx.arc(m.x, m.y, m.r + 4, 0, Math.PI * 2)
          ctx.strokeStyle = COLOUR.hot
          ctx.lineWidth = 1.5
          ctx.globalAlpha = 0.9
          ctx.stroke()
          ctx.globalAlpha = 1
        }
      }

      if (!reduced && !document.hidden) rafRef.current = requestAnimationFrame(draw)
    }

    draw()
    const onResize = () => { size(); draw() }
    const onVisible = () => {
      if (!document.hidden && !reduced) rafRef.current = requestAnimationFrame(draw)
    }
    window.addEventListener('resize', onResize)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [seed, reduced, graph, height])

  if (!graph || seed.length === 0) {
    return (
      <div className="flex items-center justify-center text-erp-xs text-muted-foreground"
           style={{ height }}>
        No social threads in the current window, so there is nothing moving to draw.
      </div>
    )
  }

  const unanswered = seed.filter((s) => s.unanswered).length
  const unread = seed.filter((s) => s.hue === 'unread').length

  return (
    <div>
      <canvas ref={canvasRef} className="block w-full" style={{ height }}
              role="img"
              aria-label={`Live social pulse: ${seed.length} threads drawn, `
                + `${unanswered} above the response threshold with nothing drafted, `
                + `${unread} never read. Height is threat, size is engagements, speed is velocity.`} />
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-erp-xs text-muted-foreground">
        <Swatch colour="var(--dash-hot, #e11d48)" filled>Above the threshold</Swatch>
        <Swatch colour="var(--dash-cool, #0284c7)" filled>Being watched</Swatch>
        <Swatch colour="var(--dash-unread, #94a3b8)">Never read — unknown, not safe</Swatch>
        <span className="inline-flex items-center gap-1.5">
          <svg width={16} height={16} aria-hidden>
            <circle cx={8} cy={8} r={3} fill="var(--dash-hot, #e11d48)" fillOpacity={0.5} />
            <circle cx={8} cy={8} r={6.5} fill="none" stroke="var(--dash-hot, #e11d48)" strokeWidth={1.5} />
          </svg>
          Ringed = nothing drafted against it
        </span>
        <span>Higher is more dangerous · bigger is more engagement · faster is spreading faster</span>
        {reduced && <span>Motion is off because your system asks for reduced motion.</span>}
      </div>
    </div>
  )
}

function Swatch({ colour, filled, children }: {
  colour: string; filled?: boolean; children: React.ReactNode
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <svg width={12} height={12} aria-hidden>
        <circle cx={6} cy={6} r={4.5}
                fill={filled ? colour : 'none'} fillOpacity={filled ? 0.5 : 0}
                stroke={colour} strokeWidth={1.5} />
      </svg>
      {children}
    </span>
  )
}
